import classNames from "classnames";
import { debounce } from "lodash-es";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import { UPDATE_TEST_ENTRIES_SUCCESS } from "../../../../../store/actions";
import { Context } from "../../../../../store/store";
import { mergeEntries } from "../../../../../store/utils";
import { API_URLS } from "../../../../../utils/api-constants";
import { axiosPost } from "../../../../../utils/queries";
import { BottomBar } from "../../components/bottom-bar";
import { Footer } from "../../components/footer";

import {
  getGroupIdFromUrl,
  getParticipationFlowUrlForCurrentSession,
} from "../../utils";
import {
  AiResponseBase,
  AreaSliderAiResponse,
  getAnswerSuggestion,
  SliderAiResponse,
  transcribeBlob,
  WordCloudAiResponse,
} from "./ai-helper";
import { oneTimeAxiosPost } from "./request-utils";
import TestItem from "./TestItem";
import { useItemsIterator } from "./useItemsIterator";
import { useIsDesktopOrTablet } from "../../../../../hooks";
import { StoreContextType } from "@/store/types";
import { DtoPublicTest, DtoTestSet, DtoTestSetItem } from "@/types/dto-types";
import { GenericObject } from "@/types";
import { JOURNEY_SUB_URL, TEST_TYPES } from "../../../constants";
import { useUrlState } from "../../hooks";
import { Module, ModuleEnum } from "../../../../../constants";
import { ParticipationHeader } from "./ParticipationHeader";
import { isDebugMode } from "../../../../../utils/env";

import "./styles.scss";

export function TestSet() {
  const [state, dispatch] = useContext<StoreContextType>(Context as any);
  const {
    testId,
    sessionId,
    subPage: testSetId,
    subStep: itemId,
    navigateToParticipationPage,
    navigateToUrl,
    module,
  } = useUrlState();
  const { t } = useTranslation();
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSaving, setIsSaving] = useState(false); //eslint-disable-line no-unused-vars
  const [itemSequence, setItemSequence] = useState(0);

  const { publicTest } = state;
  const isDebug = isDebugMode();

  const currentSet = useMemo(() => {
    if (module === Module.Reflection) {
      return publicTest.reflectionTestSets.find(
        set => set._id === testSetId
      ) as DtoTestSet;
    }

    if (module === Module.Solution) {
      return publicTest.solutionTestSets.find(
        set => set._id === testSetId
      ) as DtoTestSet;
    }

    return publicTest.sets.find(set => set._id === testSetId) as DtoTestSet;
  }, [testSetId, publicTest, module]);

  function navigateBackToSolutionJourney() {
    navigateToParticipationPage({
      subPage: JOURNEY_SUB_URL,
      subStep: currentSet._id,
    });
  }

  function handleOnComplete() {
    if (module === Module.Solution) {
      saveEntireDataSet();
      navigateBackToSolutionJourney();
    } else {
      setIsSaving(true);
      saveEntireDataSet().then(publicTest => {
        setIsSaving(false);
        navigateToUrl(
          getParticipationFlowUrlForCurrentSession(
            publicTest as DtoPublicTest,
            sessionId,
            module as ModuleEnum
          )
        );
      });
    }
  }

  const currentEntries = useMemo(() => {
    return publicTest.entries.find(
      e => e.testSetId === currentSet._id
    ) as GenericObject;
  }, [publicTest, currentSet]);

  const itemsIterator = useItemsIterator({
    currentSet,
    currentEntries,
    onComplete: handleOnComplete,
    onNextItem: handleItemNavigation,
    onPreviousItem: handleItemNavigation,
    initialItemId: itemId,
  });

  function handleItemNavigation(item: DtoTestSetItem) {
    if (module === Module.Solution) {
      navigateBackToSolutionJourney();
    } else {
      navigateToParticipationPage({
        subPage: testSetId,
        subStep: item._id,
      });
    }
  }

  const updatePartialData = useMemo(
    () =>
      debounce(data => {
        oneTimeAxiosPost(API_URLS.PUBLIC_TEST, { data })
          .then(response => {
            dispatch({ type: UPDATE_TEST_ENTRIES_SUCCESS, payload: response });
            setIsSaving(false);
          })
          .catch(e => {
            if (!axios.isCancel(e)) {
              toast.error(
                t(
                  "frontend.networkError",
                  "Error: Blocked by Firewall or Internet connection error. Please contact your IT administrator"
                )
              );
            }
            setIsSaving(false);
          });
      }, 500),
    [setIsSaving, t, dispatch]
  );

  function saveEntireDataSet() {
    const data = {
      ...currentEntries,
      testId,
      testSetId: currentSet._id,
      sessionId: sessionId,
      completed: true,
    };

    return axiosPost(API_URLS.PUBLIC_TEST, { data })
      .then(response => {
        dispatch({ type: UPDATE_TEST_ENTRIES_SUCCESS, payload: response });
        return {
          ...state.publicTest,
          entries: mergeEntries(response, state.publicTest.entries),
        };
      })
      .catch(e => {
        // todo handle errors sentry;
      });
  }

  const isDesktopOrTablet = useIsDesktopOrTablet();

  function handleItemChange(data: GenericObject, skipSave: boolean) {
    const body = {
      ...currentEntries,
      ...data,
      testId,
      testSetId: currentSet._id,
      sessionId: sessionId,
      groupId: getGroupIdFromUrl(),
      language: publicTest.languageId,
      module,
    };

    dispatch({ type: UPDATE_TEST_ENTRIES_SUCCESS, payload: body });

    if (!skipSave) {
      setIsSaving(true);
      updatePartialData(body);
    }
  }

  async function transcribeRecordedAudio(blob: Blob) {
    let transcribedText = "";
    let toastId = isDebug
      ? toast.info(
          t(
            "frontend.test.recording.transcribingRecording",
            "Transcribing your recording..."
          ),
          {
            autoClose: false,
          }
        )
      : undefined;

    let data;

    try {
      data = await transcribeBlob(blob, publicTest.languageId);
      transcribedText = data.text;
    } catch {
      toast.error(
        t(
          "frontend.test.recording.errorTranscribingRecording",
          "Error processing your recording. Please try again."
        )
      );
    } finally {
      toast.dismiss(toastId);
    }

    if (isDebug) {
      toast.info(
        `Transcription result for lang ${data.language}: ${transcribedText}`,
        {
          autoClose: false,
        }
      );
    }

    return transcribedText;
  }

  async function tryGetAnswerSuggestion(text: string) {
    let data = null;

    const toastId = isDebug
      ? toast.info(
          t(
            "frontend.test.recording.processingRecording",
            "AI stuff is working on generating a reply for you..."
          ),
          {
            autoClose: false,
          }
        )
      : undefined;
    try {
      data = await getAnswerSuggestion(itemsIterator.currentItem._id, text);
      if (isDebug) {
        toast.info(`Received response: ${JSON.stringify(data, null, 2)}`, {
          autoClose: false,
          style: { whiteSpace: "pre", fontFamily: "monospace" },
        });
      }
    } catch (error) {
      toast.error(
        t(
          "frontend.test.recording.errorGeneratingReply",
          "Error generating reply. Please try again."
        )
      );
    } finally {
      toast.dismiss(toastId);
    }

    return data;
  }

  async function handleAudioReady(blob: Blob) {
    if (itemsIterator.isVideoStep) {
      itemsIterator.handleNextClick();
    }

    const transcribedText = await transcribeRecordedAudio(blob);

    /*In case of text component just set value and skip AI suggestion call*/
    if (itemsIterator.currentItem.type === TEST_TYPES.TEXT) {
      handleItemChange(
        {
          [`${itemsIterator.currentItem.dbColumn}`]: transcribedText,
        },
        false
      );
      return;
    }

    const suggestionData = (await tryGetAnswerSuggestion(
      transcribedText
    )) as AiResponseBase | null;
    if (suggestionData) {
      if (suggestionData.error) {
        toast.error(suggestionData.error.message);
        return;
      }

      if (
        itemsIterator.currentItem.type === TEST_TYPES.SingleSlider ||
        itemsIterator.currentItem.type === TEST_TYPES.DoubleSlider
      ) {
        handleItemChange(
          {
            [`${itemsIterator.currentItem.dbColumn}`]: (
              suggestionData as SliderAiResponse
            ).value,
          },
          false
        );
      } else if (itemsIterator.currentItem.type === TEST_TYPES.AreaSlider) {
        const response =
          suggestionData as AreaSliderAiResponse as AreaSliderAiResponse;
        handleItemChange(
          {
            [`${itemsIterator.currentItem.dbColumn}`]: [
              response.implementationQuality,
              response.importance,
            ],
          },
          false
        );
      } else if (itemsIterator.currentItem.type === TEST_TYPES.WordCloud) {
        handleItemChange(
          {
            [`${itemsIterator.currentItem.dbColumn}Raw`]: JSON.stringify(
              (suggestionData as WordCloudAiResponse).terms?.map(term => ({
                label: term,
              }))
            ),
          },
          false
        );
      }

      setItemSequence(current => current + 1);
    }
  }

  function handleFooterPreviousClick() {
    if (module === Module.Solution && itemsIterator.isVideoStep) {
      navigateBackToSolutionJourney();
    } else {
      itemsIterator.handlePreviousClick();
    }
  }

  if (!currentSet) {
    return <div>Error in set loading...</div>;
  }

  return (
    <div className="tg_set-page tg_page tg_page-layout">
      <ParticipationHeader
        publicTest={publicTest}
        currentSet={currentSet}
        module={module as ModuleEnum}
        currentItemIndex={itemsIterator.currentItemIndex}
        isSaving={isSaving}
      />
      <div className="tg_main">
        <div className="tg_column" style={{ height: "100%" }}>
          <TestItem
            key={`${currentSet._id}-${itemsIterator.currentItem._id}-${itemSequence}`}
            className={classNames("tg_form-item")}
            item={itemsIterator.currentItem}
            isVideoStep={itemsIterator.isVideoStep}
            initialValue={
              currentEntries?.[`${itemsIterator.currentItem.dbColumn}`]
            }
            initialRawValue={
              currentEntries?.[`${itemsIterator.currentItem.dbColumn}Raw`]
            }
            onChange={(e, skipSave) => handleItemChange(e, !!skipSave)}
            onNextClick={itemsIterator.handleNextClick}
            onBlur={() => handleItemChange({}, false)}
          />
        </div>
      </div>
      <Footer
        onPreviousClick={handleFooterPreviousClick}
        onNextClick={itemsIterator.handleNextClick}
        isPreviousDisabled={
          module === Module.Solution ? false : !itemsIterator.canGoPreviousItem
        }
        isNextDisabled={!itemsIterator.canGoToNextItem}
        isMicrophoneDisabled={
          !(
            itemsIterator.currentItem.prompt ||
            itemsIterator.currentItem.type === TEST_TYPES.TEXT
          )
        }
        onAudioReady={handleAudioReady}
      />
      {isDesktopOrTablet && <BottomBar isWizard />}
    </div>
  );
}
